import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, NgZone, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { SnackbarV3Service } from '@gea/digital-ui-lib';
import { PermissionService } from '../../core/services/permission.service';

const loginRedirectMessageKey = 'ceh-login-redirect-message';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit {
  public isSubmitting = false;
  public isCheckingAdmin = true;
  public isCreateAdminMode = false;
  public errorMessage = '';
  public infoMessage = '';
  public showPassword = false;
  public readonly loginForm: FormGroup;
  private readonly permissionService = inject(PermissionService);

  constructor(
    private readonly fb: FormBuilder,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly snackbarService: SnackbarV3Service,
  ) {
    this.loginForm = this.fb.group({
      userName: ['', [Validators.required, Validators.minLength(2)]],
      password: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  public ngOnInit(): void {
    this.showLoginRedirectMessage();

    if (this.authService.isAuthenticated()) {
      void this.router.navigateByUrl(this.permissionService.getLandingRoute(), { replaceUrl: true });
      return;
    }

    this.authService.isAdminPresent().subscribe({
      next: (response) => {
        this.isCheckingAdmin = false;
        this.isCreateAdminMode = !response.isAdminExist;
        if (this.isCreateAdminMode) {
          this.loginForm.get('userName')?.patchValue('admin');
          this.loginForm.get('userName')?.disable();
        }
        this.reloadContent();
      },
      error: () => {
        this.isCheckingAdmin = false;
        this.errorMessage = 'Unable to load authentication state. Please try again.';
        this.reloadContent();
      },
    });
  }

  public submit(): void {
    this.errorMessage = '';
    this.infoMessage = '';

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const { userName, password } = this.loginForm.getRawValue();

    if (this.isCreateAdminMode) {
      this.authService.createInitialAdmin(userName ?? '', password ?? '').subscribe({
        next: () => {
          this.infoMessage = 'Admin account created. Please sign in with the new account.';
          this.isCreateAdminMode = false;
          this.loginForm.reset();
          this.loginForm.get('userName')?.enable();
          this.router.navigateByUrl('/login', { replaceUrl: true }).then(() => {
            this.reloadContent();
            this.isSubmitting = false;
          });
        },
        error: (error) => {
          this.errorMessage = error?.error?.message || error?.error || 'Unable to create the initial admin user.';
          this.reloadContent();
          this.isSubmitting = false;
        },
      });

      return;
    }
    this.authService.login(userName ?? '', password ?? '').subscribe({
      next: () => {
        this.ngZone.run(() => {
          // If returnUrl exists, use it. Otherwise, use the role-based landing route.
          const returnUrl =  this.permissionService.getLandingRoute();
          this.router.navigateByUrl(returnUrl, { replaceUrl: true }).then(() => {
            this.reloadContent();
            this.isSubmitting = false;
          });
        });
      },
      error: (error) => {
        this.errorMessage = error?.error?.message || error?.error || error?.message || 'Please enter a valid username and password.';
        this.reloadContent();
        this.isSubmitting = false;
      },
    });
  }

  public togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  private reloadContent(): void {
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  private showLoginRedirectMessage(): void {
    const rawMessage = sessionStorage.getItem(loginRedirectMessageKey);

    if (!rawMessage) {
      return;
    }

    sessionStorage.removeItem(loginRedirectMessageKey);

    try {
      const message = JSON.parse(rawMessage);
      setTimeout(() => {
        this.snackbarService.add({
          detail: message?.detail || 'Session expired',
          summary: message?.summary || 'Error',
          severity: message?.severity || 'error',
        });
      });
    } catch {
      this.snackbarService.add({
        detail: 'Session expired',
        summary: 'Error',
        severity: 'error',
      });
    }
  }

  public hasError(controlName: 'userName' | 'password'): boolean {
    const control = this.loginForm.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  public goToChangePassword(): void {
    const userName = this.loginForm.get('userName')?.value;
    if (!userName) {
      this.errorMessage = 'Please enter a username first.';
      this.reloadContent();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.authService.isPasswordExist(userName).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.reloadContent();
        this.router.navigate(['/change-password'], { 
          state: { 
            userName: userName, 
            isPasswordExist: response.isPasswordExist 
          } 
        }).then(() => {
          this.isSubmitting = false;
          this.reloadContent();
        });
      },
      error: (error) => {
        this.isSubmitting = false;
        this.errorMessage = error?.error?.message || error?.error || 'Unable to verify user. Please try again.';
        this.reloadContent();
      }
    });
  }
}

